import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { apiError } from "../../shared/api/client";
import { platformApi, platformQueryKeys, type PlatformTenant } from "../../shared/api/platform";
import {
  RESULTS_LOOKUP_OFF_WARNING,
  ResultsLookupStatusBadge,
  resultsLookupReasonText,
  turnOnBlockers,
} from "../website/ResultsLookupStatus";

/**
 * Admin Addy tích/bỏ tích hộ ô "Cho phép bệnh nhân tra cứu kết quả khám trên website" của một phòng
 * khám (#19191) — nút dừng khẩn cấp khi nghi lộ dữ liệu mà không liên lạc được phòng khám. Lý do
 * bắt buộc: nó được ghi vào nhật ký kiểm toán của chính phòng khám, cùng tên người bấm.
 */
export function PlatformResultsLookupDialog({
  tenant,
  open,
  onOpenChange,
}: {
  tenant: PlatformTenant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");

  const q = useQuery({
    queryKey: platformQueryKeys.resultsLookup(tenant.id),
    queryFn: () => platformApi.resultsLookup(tenant.id),
    enabled: open,
  });

  const save = useMutation({
    mutationFn: (enabled: boolean) =>
      platformApi.setResultsLookup(tenant.id, enabled, reason.trim()),
    onSuccess: (data) => {
      qc.setQueryData(platformQueryKeys.resultsLookup(tenant.id), data);
      setReason("");
    },
  });

  const d = q.data;
  const next = d ? !d.enabled : null;
  const reasonText = d ? resultsLookupReasonText(d) : null;
  // #19614 — the same rule as the clinic's own screen: "bật hộ" is not offered while no OTP would
  // arrive. "Tắt ngay" never waits on anything.
  const blockers = d ? turnOnBlockers(d) : [];
  const turnOnLocked = next === true && blockers.length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setReason("");
          save.reset();
        }
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tra cứu kết quả trên website — {tenant.name}</DialogTitle>
          <DialogDescription>
            Đổi hộ phòng khám ô “Cho phép bệnh nhân tra cứu kết quả khám trên website”.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {q.isLoading && <Skeleton className="h-5 w-40" />}
          {q.isError && (
            <p className="text-sm text-destructive">{apiError(q.error).detail}</p>
          )}
          {d && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span>Hiện tại:</span>
                <ResultsLookupStatusBadge data={d} />
              </div>
              {reasonText && <p className="text-sm text-muted-foreground">{reasonText}</p>}
              {turnOnLocked && (
                <div
                  role="status"
                  data-testid="platform-lookup-blockers"
                  className="grid gap-1 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900"
                >
                  <p className="font-medium">Chưa bật hộ được — phòng khám chưa gửi được mã xác thực:</p>
                  <ul className="grid gap-0.5">
                    {blockers.map((b) => (
                      <li key={b.key}>
                        • {b.text}
                        {b.details?.map((t) => (
                          <span key={t} className="block pl-3 text-xs">
                            {t}
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {d.enabled && (
                <p
                  role="alert"
                  className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900"
                >
                  {RESULTS_LOOKUP_OFF_WARNING}
                </p>
              )}
              <label className="grid gap-1.5 text-sm">
                <span className="font-medium">Lý do (ghi vào nhật ký của phòng khám)</span>
                <Textarea
                  value={reason}
                  maxLength={500}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={
                    d.enabled
                      ? "Ví dụ: nghi lộ dữ liệu, chưa liên lạc được phòng khám"
                      : "Ví dụ: phòng khám nhờ bật lại qua điện thoại"
                  }
                />
              </label>
              {save.isError && (
                <p className="text-sm text-destructive">{apiError(save.error).detail}</p>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Đóng
          </Button>
          {d && next !== null && (
            <Button
              size="sm"
              variant={next ? "default" : "destructive"}
              disabled={save.isPending || reason.trim().length === 0 || turnOnLocked}
              onClick={() => save.mutate(next)}
            >
              {save.isPending ? "Đang lưu…" : next ? "Bật hộ phòng khám" : "Tắt ngay"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
